import { S3_BASE_URL } from "./constants";
import { metadataSchema, snapshotSchema } from "./schemas";
import type { ParsedRankingReference } from "../types";

const MAX_RESPONSE_BYTES = 20 * 1024 * 1024;
const REQUEST_TIMEOUT_MS = 20_000;

export class SourceFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SourceFetchError";
  }
}

function sourceUrl(reference: ParsedRankingReference, file: "metadata.json" | "all.json") {
  return `${S3_BASE_URL}/rankings/${reference.raceId}/${reference.rankingType}/${file}`;
}

async function fetchJson(url: string, signal?: AbortSignal): Promise<unknown> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });

  try {
    const response = await fetch(url, {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new SourceFetchError(`來源回應 HTTP ${response.status}，請確認賽事資料是否存在。`);
    }
    const type = response.headers.get("content-type") ?? "";
    if (!type.toLowerCase().includes("json")) {
      throw new SourceFetchError("來源回應不是 JSON，已停止載入。");
    }
    const length = Number(response.headers.get("content-length") ?? 0);
    if (length > MAX_RESPONSE_BYTES) {
      throw new SourceFetchError("來源檔案超過 20 MB 安全上限，已停止載入。");
    }
    const text = await response.text();
    if (new Blob([text]).size > MAX_RESPONSE_BYTES) {
      throw new SourceFetchError("來源檔案超過 20 MB 安全上限，已停止載入。");
    }
    return JSON.parse(text) as unknown;
  } catch (error) {
    if (error instanceof SourceFetchError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new SourceFetchError("連線逾時，請稍後再試；若已有快照會繼續顯示舊資料。");
    }
    if (error instanceof SyntaxError) {
      throw new SourceFetchError("來源 JSON 無法解析，已保留目前有效資料。");
    }
    throw new SourceFetchError(
      "無法讀取 TATTA S3 資料，可能是網路或瀏覽器 CORS 政策造成。",
    );
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

export async function fetchMetadata(reference: ParsedRankingReference, signal?: AbortSignal) {
  const raw = await fetchJson(sourceUrl(reference, "metadata.json"), signal);
  const result = metadataSchema.safeParse(raw);
  if (!result.success) throw new SourceFetchError("Metadata 格式與預期不符，已停止載入。");
  if (result.data.race.id !== reference.raceId) {
    throw new SourceFetchError("Metadata 賽事 ID 與輸入網址不一致，已停止載入。");
  }
  return result.data;
}

export async function fetchSnapshot(reference: ParsedRankingReference, signal?: AbortSignal) {
  const raw = await fetchJson(sourceUrl(reference, "all.json"), signal);
  const result = snapshotSchema.safeParse(raw);
  if (!result.success) throw new SourceFetchError("跑者快照格式與預期不符，已停止載入。");
  return result.data;
}
